// ============================================================
// defence/sync - the pieces of `useDefenceSync` that are not React.
//
// Reading a snapshot of the defence/ subcollection into the local map
// (keeping late-arriving combatants, dropping stale orphans, honouring
// echo prevention), the expiry sweep, and the server writes with their
// diagnostics. The hook owns the state, the refs and the lifecycle.
// ============================================================
import { canonicalJson } from '../../engine/canonicalJson';
import type { MutableRefObject } from 'react';
import { diag, errorSummary } from '../../diagnostics/log';
import { DEFENCE_TIMEOUT_MS } from '../../engine/constants';
import type { EchoPrevention } from '../useEchoPrevention';
import type * as mpModule from '../../multiplayer';
import { keepUnchangedMap } from '../../utils/keepUnchanged';

export type DefenceDoc = Record<string, unknown> & { createdAt?: number };
type Mp = typeof mpModule;

const CLEANUP_INTERVAL_MS = 30_000; // Check for stale defences every 30s

/** A copy of the map without these ids. */
export function withoutIds(map: Map<string, DefenceDoc>, ids: Iterable<string>): Map<string, DefenceDoc> {
  const next = new Map(map);
  for (const id of ids) next.delete(id);
  return next;
}

/** The prompts created after a history snapshot's watermark - the ones an undo takes back. */
function createdSince(pending: Map<string, DefenceDoc>, watermark: number): string[] {
  return [...pending].filter(([, doc]) => (doc.createdAt ?? 0) > watermark).map(([tabId]) => tabId);
}

/**
 * THE PROMPTS THE SERVER REFUSED (2026-10-30): written here, never stored there - a Firestore write refused a nested
 * array, and the "No Defence" claim then waited for a document that would never come and dropped the hits. A
 * refused prompt exists only in this browser, so it is answered here (defence/flow `isShared`).
 */
const refused = new Set<string>();

/** Did the server refuse this browser's last write of the prompt? */
export const promptRefused = (tabId: string): boolean => refused.has(tabId);

/** Write a prompt to the server; a failure is logged and remembered, never thrown. */
function writeDefence(mp: Mp, sessionId: string, tabId: string, data: DefenceDoc): void {
  diag.info('defence', `writing prompt ${tabId}`);
  refused.delete(tabId);
  mp.setPendingDefence(sessionId, tabId, data)
    .then(() => diag.info('defence', `prompt ${tabId} acknowledged by the server`))
    .catch((err) => {
      refused.add(tabId);
      console.error('[Defence] write error:', err);
      diag.error('defence', `prompt ${tabId} write failed`, errorSummary(err));
    });
}

/** Delete a prompt on the server; a failure is logged, never thrown. */
function deleteDefence(mp: Mp, sessionId: string, tabId: string): void {
  diag.info('defence', `clearing ${tabId}`);
  mp.clearPendingDefence(sessionId, tabId).catch((err) => {
    console.error('[Defence] clear error:', err);
    diag.error('defence', `clear of ${tabId} failed`, errorSummary(err));
  });
}

type SetPending = (u: Map<string, DefenceDoc> | ((prev: Map<string, DefenceDoc>) => Map<string, DefenceDoc>)) => void;

/** The local map and, in a session, the server behind it. */
export interface PromptStore {
  sessionId: string | null;
  mp: Mp;
  echo: EchoPrevention;
  setPendingDefences: SetPending;
}

/** Create a prompt: into the local map in solo, onto the server in a session. */
export function createPrompt(s: PromptStore, tabId: string, data: DefenceDoc): void {
  // SOLO ONLY. Seeding the map here in a SESSION is a race with the
  // backend, and it cost a real game: the prompt appears the instant the
  // shot is fired, so the attacker can answer it before the document
  // exists - and `resolvePendingDefence` bails on a document that is not
  // there (`if (!current) return undefined`). The caller then returns
  // early, the hits are never applied, and the entry is never cleared, so
  // the prompt sits there for ever (2026-08-18).
  //
  // In a session the backend is the source of truth and the listener puts
  // it in the map, which is how it worked before solo needed a queue.
  if (!s.sessionId) {
    s.setPendingDefences((prev) => new Map(prev).set(tabId, data));
    return;
  }
  s.echo.setCache('defence/' + tabId, data);
  writeDefence(s.mp, s.sessionId, tabId, data);
}

/** Clear a prompt from the local map, and from the server in a session. */
export function clearPrompt(s: PromptStore, tabId: string): void {
  s.setPendingDefences((prev) => (prev.has(tabId) ? withoutIds(prev, [tabId]) : prev));
  if (!s.sessionId) return;
  s.echo.deleteCache('defence/' + tabId);
  deleteDefence(s.mp, s.sessionId, tabId);
}

/**
 * Clear the prompts created after the watermark - or every prompt, for a
 * snapshot with none (an older client's). Session only.
 */
export function clearPromptsSince(
  s: PromptStore,
  pendingRef: MutableRefObject<Map<string, DefenceDoc>>,
  watermark: number | null | undefined,
  clearAll: () => void,
): void {
  if (!s.sessionId) return;
  if (watermark == null) return clearAll();
  const doomed = createdSince(pendingRef.current, watermark);
  if (!doomed.length) return;
  for (const tabId of doomed) {
    s.echo.deleteCache('defence/' + tabId);
    s.mp.clearPendingDefence(s.sessionId, tabId).catch((err) => console.error('[Defence] clear error:', err));
  }
  // Same reason as the expiry sweep: the mirror must forget them now, not at the next render.
  pendingRef.current = withoutIds(pendingRef.current, doomed);
  s.setPendingDefences(pendingRef.current);
}

/** The prompts older than the timeout, as of `now`. */
function expiredOf(pending: Map<string, DefenceDoc>, now: number): [string, DefenceDoc][] {
  return [...pending].filter(([, data]) => data.createdAt && now - data.createdAt > DEFENCE_TIMEOUT_MS);
}

/** What a snapshot reading needs from the hook. */
interface SnapshotReader {
  /** The combatants this client knows about. */
  known: Set<string>;
  echo: EchoPrevention;
  /** The map as it stood when the listener subscribed. */
  previous: Map<string, DefenceDoc>;
}

/**
 * A COMBATANT THIS CLIENT HAS NOT BEEN TOLD ABOUT YET IS NOT AN ORPHAN
 * (v8.160.0). The old guard was "my roster is not empty", and a joining
 * player satisfies that the moment their FIRST combatant arrives - they
 * then deleted a live prompt for somebody they had not heard of yet.
 *
 * Age is the honest discriminator: only a document older than the timeout
 * can be a leftover worth deleting. Anything else is KEPT, so it renders
 * the moment the roster catches up.
 */
function isStaleOrphan(r: SnapshotReader, data: DefenceDoc): boolean {
  const age = Date.now() - (typeof data?.createdAt === 'number' ? data.createdAt : Date.now());
  return r.known.size > 0 && age > DEFENCE_TIMEOUT_MS;
}

/** One document of the snapshot into `next`, or onto the orphan list. */
function readEntry(
  r: SnapshotReader,
  tabId: string,
  data: DefenceDoc,
  out: { next: Map<string, DefenceDoc>; orphanIds: string[] },
) {
  if (!r.known.has(tabId)) {
    if (isStaleOrphan(r, data)) out.orphanIds.push(tabId);
    else out.next.set(tabId, data);
    return;
  }
  // Echo prevention: data unchanged - keep the existing local entry if present.
  const key = 'defence/' + tabId;
  // The same canonical text the echo cache keeps (v8.393.3).
  const json = canonicalJson(data);
  if (json === r.echo.getCache(key)) {
    out.next.set(tabId, r.previous.get(tabId) || data);
    return;
  }
  r.echo.cacheRef.current[key] = json;
  out.next.set(tabId, data);
}

/** Read a snapshot into the next map, and the orphans to delete. */
export function readSnapshot(r: SnapshotReader, entries: { id: string; data: DefenceDoc }[]) {
  const out = { next: new Map<string, DefenceDoc>(), orphanIds: [] as string[] };
  entries.forEach((d) => readEntry(r, d.id, d.data, out));
  return out;
}

/**
 * A hidden victim's prompt is LOCAL ONLY (plan map-display, G1): it is never
 * written, since its key and its frozen target are the hidden figure's, and
 * every member reads the prompts. A server read does not list it, so it is
 * carried over from the live map rather than dropped.
 */
export function keepLocalOnly(next: Map<string, DefenceDoc>, live: Map<string, DefenceDoc>): Map<string, DefenceDoc> {
  let out = next;
  for (const [id, d] of live) {
    if (!d?.localOnly || next.has(id)) continue;
    if (out === next) out = new Map(next);
    out.set(id, d);
  }
  return out;
}

/** The view, as the diagnostics report needs it: which prompts, and whether each is still open (v8.310.16). */
export function viewLine(next: Map<string, DefenceDoc>): string {
  const ids = [...next].map(([id, d]) => `${id}${d?.resolvedBy ? ' (resolved by ' + d.resolvedBy + ')' : ''}`);
  return `view: ${ids.join(', ') || 'none'}`;
}

/** The hook's state and refs the listener and the sweep work on. */
export interface SyncHandles {
  sessionId: string;
  mp: Mp;
  echo: EchoPrevention;
  tabIdsRef: MutableRefObject<Set<string>>;
  pendingRef: MutableRefObject<Map<string, DefenceDoc>>;
  onExpireRef: MutableRefObject<((combatantId: string, doc: DefenceDoc) => void) | undefined>;
  setPendingDefences: (
    u: Map<string, DefenceDoc> | ((prev: Map<string, DefenceDoc>) => Map<string, DefenceDoc>),
  ) => void;
}

/**
 * AUDITED AND DELIBERATELY LEFT ALONE (v8.25.0). Every other listener that
 * rebuilt state from a snapshot list needed teaching that a mid-burst view
 * is not the whole truth. This one does not: a defence prompt is transient,
 * it is re-created by the flow that wanted it, and this loop DELETES
 * ORPHANS on purpose.
 */
function takeSnapshot(h: SyncHandles, previous: Map<string, DefenceDoc>, entries: { id: string; data: DefenceDoc }[]) {
  const read = readSnapshot({ known: h.tabIdsRef.current, echo: h.echo, previous }, entries);
  const { orphanIds } = read;
  // A view that changes no prompt keeps the map, renders nothing and logs nothing (plan sync-listeners): every write
  // anywhere re-delivered every prompt as a new map, and the shell rendered from the root for each.
  const next = keepUnchangedMap(h.pendingRef.current, keepLocalOnly(read.next, h.pendingRef.current));
  if (next !== h.pendingRef.current) {
    h.pendingRef.current = next;
    h.setPendingDefences(next);
    diag.info('defence', viewLine(next));
  }
  // Delete orphaned docs - BUT ONLY IF WE KNOW THE ROSTER. A client whose
  // roster has not arrived cannot tell "gone" from "not told yet", and a
  // joiner deleted the GM's live prompt that way (v8.29.0).
  if (h.tabIdsRef.current.size === 0) return;
  for (const id of orphanIds) {
    h.echo.deleteCache('defence/' + id);
    h.mp.clearPendingDefence(h.sessionId, id).catch(() => {});
  }
}

/**
 * AN EXPIRY IS AN ANSWER, NOT AN ERASURE. A defender who says nothing gets
 * hit: the expired prompt is handed to `onExpire` to resolve as "no
 * reaction" (the old sweep deleted it, and the shot ceased to have
 * happened). Every client sweeps; `resolvePendingDefence` is
 * first-writer-wins, so exactly one resolves.
 *
 * Decided OUTSIDE any state updater (React may run one twice), and dropped
 * from the mirror IMMEDIATELY: a second tick before the re-render must not
 * resolve the same reaction twice.
 */
function sweep(h: SyncHandles): void {
  const expired = expiredOf(h.pendingRef.current, Date.now());
  if (!expired.length) return;
  for (const [tabId, data] of expired) {
    h.echo.deleteCache('defence/' + tabId);
    if (h.onExpireRef.current) h.onExpireRef.current(tabId, data);
    else h.mp.clearPendingDefence(h.sessionId, tabId).catch(() => {});
  }
  const ids = expired.map(([tabId]) => tabId);
  h.pendingRef.current = withoutIds(h.pendingRef.current, ids);
  h.setPendingDefences((prev) => withoutIds(prev, ids));
}

/** Subscribe to the session's prompts and start the expiry sweep. Returns the teardown. */
export function startDefenceSync(h: SyncHandles, previous: Map<string, DefenceDoc>): () => void {
  const unsub = h.mp.onDefenceUpdate(h.sessionId, ({ entries }) =>
    takeSnapshot(h, previous, entries as { id: string; data: DefenceDoc }[]),
  );
  const timer = setInterval(() => sweep(h), CLEANUP_INTERVAL_MS);
  return () => {
    unsub();
    clearInterval(timer);
  };
}
